import { AIRLINE_CODE_JETSTAR } from 'constants/flight'
import type { IntlShape } from 'react-intl'

export interface FlightTileBaggage {
  carryOn: boolean
  checked: boolean
  carryOnLabel?: string
  checkedLabel?: string
}

export type BaggageSummaryInclusion = Pick<App.LegInclusion, 'description' | 'extras'>

const carryOnTypes = new Set(['carry_on_bag', 'carry_on_baggage'])

function isIncluded(feature: Pick<App.FareFamilyFeature, 'status'>) {
  return feature.status === 'INCLUDED'
}

/**
 * Providers disagree on the carry-on feature type: Sabre emits `carry_on_bag`, Jetstar-direct
 * emits `carry_on_baggage`. Both mean the same thing, so always match on either.
 */
export function isCarryOnFeatureType(type: App.FareFamilyFeature['type']) {
  return type !== undefined && carryOnTypes.has(type)
}

/**
 * Returns an explicit baggage decision only when the selected fare family declares the
 * corresponding feature. `undefined` deliberately means the provider gave us no fare-family
 * information, so callers can fall back to the journey-level baggage data.
 */
export function getFareFamilyBaggageInclusion(fareFamily?: App.FlightFareFamily) {
  const features = fareFamily?.fareFamily.fareFeatures ?? []
  const inclusionFor = (matches: (feature: App.FareFamilyFeature) => boolean): boolean | undefined => {
    const matchingFeatures = features.filter(matches)
    if (matchingFeatures.length === 0) {
      return undefined
    }

    return matchingFeatures.some(isIncluded)
  }

  return {
    carryOn: inclusionFor(feature => isCarryOnFeatureType(feature.type)),
    checked: inclusionFor(feature => feature.type === 'checked_baggage'),
  }
}

function getIncludedCarryOn<T extends Pick<App.FareFamilyFeature, 'type' | 'status' | 'description' | 'extras'>>(features: Array<T>) {
  return features.find(feature => isCarryOnFeatureType(feature.type) && isIncluded(feature))
}

function getIncludedCheckedBaggage<T extends Pick<App.FareFamilyFeature, 'type' | 'status' | 'description' | 'extras'>>(features: Array<T>) {
  return features.find(feature => feature.type === 'checked_baggage' && isIncluded(feature))
}

function getBaggageInformation(
  inclusion: BaggageSummaryInclusion,
  passengerType?: string,
): App.InclusionBaggageInformation | undefined {
  const items = inclusion.extras?.baggageInformation
  if (!Array.isArray(items) || items.length === 0) {
    return undefined
  }

  if (passengerType) {
    return items.find(item => item.passengerType === passengerType)
      ?? items.find(item => !item.passengerType)
  }

  const aggregate = items.find(item => !item.passengerType)
  if (aggregate) {
    return aggregate
  }

  const adult = items.find(item => item.passengerType === 'ADT')
  if (adult) {
    return adult
  }

  // Passenger-specific INF/CHD-only data is not representative for the general tile label.
  if (items.every(item => item.passengerType === 'INF' || item.passengerType === 'CHD')) {
    return undefined
  }

  return items[0]
}

function formatBaggageQuantityWeightLabel(
  quantity: number | undefined,
  unitValue: number,
  unit: string,
): string {
  const weight = `${unitValue}${unit.toLowerCase()}`
  if (quantity != null && quantity > 1) {
    return `${quantity}×${weight}`
  }

  return weight
}

export function formatBaggageSummaryLabel(
  inclusion: BaggageSummaryInclusion,
  passengerType?: string,
): string | undefined {
  const info = getBaggageInformation(inclusion, passengerType)

  if (info?.unitValue && info?.unit) {
    const quantity = info.quantity != null && info.quantity > 1 ? info.quantity : undefined
    return formatBaggageQuantityWeightLabel(quantity, info.unitValue, info.unit)
  }

  return undefined
}

export function getGenericBaggageSummaryLabel(
  baggageKind: 'carry_on' | 'checked',
  intl: IntlShape,
): string {
  if (baggageKind === 'carry_on') {
    return intl.formatMessage({
      id: 's3vzZzSn_wbWZQBRfs6V',
      defaultMessage: 'Carry-on',
      description: 'Label indicating carry-on baggage is included in flight fare',
    })
  }

  return intl.formatMessage({
    id: 'kxZtcahJ2tAn2awFzfd3',
    defaultMessage: 'Checked bag',
    description: 'Label indicating checked baggage is included in flight fare',
  })
}

export function composeBaggageDisplayLabel(categoryLabel: string, detailLabel?: string): string {
  if (!detailLabel) {
    return categoryLabel
  }

  return `${categoryLabel} ${detailLabel}`
}

export function getBaggageSummaryDisplayLabel(
  inclusion: BaggageSummaryInclusion,
  baggageKind: 'carry_on' | 'checked',
  intl: IntlShape,
  passengerType?: string,
): string {
  return composeBaggageDisplayLabel(
    getGenericBaggageSummaryLabel(baggageKind, intl),
    formatBaggageSummaryLabel(inclusion, passengerType),
  )
}

export function getFlightTileBaggageFromLeg(leg: App.Leg): FlightTileBaggage {
  const carryOn = getIncludedCarryOn(leg.inclusions)
  const checked = getIncludedCheckedBaggage(leg.inclusions)

  return {
    carryOn: Boolean(carryOn),
    checked: Boolean(checked),
    carryOnLabel: carryOn ? formatBaggageSummaryLabel(carryOn) : undefined,
    checkedLabel: checked ? formatBaggageSummaryLabel(checked) : undefined,
  }
}

/**
 * Structural rather than `Pick<App.LegFlight, ...>` because journey flights (`App.FlightV2`) and
 * leg flights (`App.LegFlight`) carry the operating airline under unrelated named types.
 */
type FlightWithOperatingAirline = { operatingAirline?: { code?: string } }

function isOperatedEntirelyByJetstarFlights(flights: Array<FlightWithOperatingAirline> | undefined): boolean {
  if (!flights?.length) {
    return false
  }

  return flights.every(flight => flight.operatingAirline?.code === AIRLINE_CODE_JETSTAR)
}

function isOperatedEntirelyByJetstar(journey: App.JourneyV2 | undefined): boolean {
  return isOperatedEntirelyByJetstarFlights(journey?.flightGroup?.flights)
}

/**
 * Jetstar states its carry-on allowance as a number of pieces rather than a single weight, so the
 * description the backend sends carries detail the generic "Carry-on" chip cannot express. Returns
 * that description so it can replace the generic label — but only when *every* segment is operated
 * by Jetstar, because one chip must not claim a Jetstar allowance for a mixed itinerary. Every
 * other carrier gets `undefined` and keeps today's chip unchanged.
 *
 * The description is already date-correct and translated by the time it reaches here; this function
 * deliberately performs no date logic of its own.
 */
export function getJetstarCarryOnLabel(
  journey: App.JourneyV2 | undefined,
  fareFamily: App.FlightFareFamily | undefined,
): string | undefined {
  if (!isOperatedEntirelyByJetstar(journey)) {
    return undefined
  }

  return getIncludedCarryOn(fareFamily?.fareFamily.fareFeatures ?? [])?.description
}

/**
 * The compact baggage chip has room for a category word, never a full allowance sentence, so
 * Jetstar's piece-based description is matched to a short label here.
 *
 * Matching on the description rather than on the feature type is deliberate. `carry_on_bag` is
 * the type every carrier emits and it says nothing about which allowance applies: a Jetstar
 * flight departing under the older weight-based conditions carries exactly the same type. The
 * description is already gated by carrier, fare family and departure date before it reaches the
 * client, so keying off it keeps this function free of date logic and makes it fail closed —
 * anything unrecognised keeps the chip it has today.
 */
const JETSTAR_UNDERSEAT_PREFIX = 'underseat'
const JETSTAR_UNDERSEAT_PLUS_OVERHEAD_PREFIX = 'underseat + overhead'

function shortenJetstarCarryOnDescription(
  description: string | undefined,
  intl: IntlShape,
): string | undefined {
  const normalizedDescription = description?.trim().toLowerCase()

  if (!normalizedDescription) {
    return undefined
  }

  // Checked before the plain underseat prefix, which it also starts with.
  if (normalizedDescription.startsWith(JETSTAR_UNDERSEAT_PLUS_OVERHEAD_PREFIX)) {
    return intl.formatMessage({
      id: 'j18pjWNpzeDhWFUjWdb1',
      defaultMessage: 'Underseat + Overhead Bag',
      description: 'Compact flight tile label for a Jetstar fare that includes both an underseat bag and an overhead locker bag',
    })
  }

  if (normalizedDescription.startsWith(JETSTAR_UNDERSEAT_PREFIX)) {
    return intl.formatMessage({
      id: 'icL069vlc3K34KwKsYWA',
      defaultMessage: 'Underseat Bag',
      description: 'Compact flight tile label for a Jetstar fare that includes an underseat bag only, with no overhead locker bag',
    })
  }

  return undefined
}

export function getJetstarCarryOnChipLabel(
  journey: App.JourneyV2 | undefined,
  intl: IntlShape,
): string | undefined {
  if (!isOperatedEntirelyByJetstar(journey)) {
    return undefined
  }

  const description = getIncludedCarryOn(journey?.fareFamily?.fareFeatures ?? [])?.description
  return shortenJetstarCarryOnDescription(description, intl)
}

/**
 * Same shortening as {@link getJetstarCarryOnChipLabel}, but for a placed order's leg (My
 * Escapes, the confirmation page, the cancellation modal) rather than a live search journey.
 * Booked legs carry the carry-on feature on `leg.inclusions`, not on a `fareFamily`.
 */
export function getJetstarCarryOnChipLabelFromLeg(
  leg: App.Leg | undefined,
  intl: IntlShape,
): string | undefined {
  if (!isOperatedEntirelyByJetstarFlights(leg?.flights)) {
    return undefined
  }

  const description = getIncludedCarryOn(leg?.inclusions ?? [])?.description
  return shortenJetstarCarryOnDescription(description, intl)
}

export function getFlightTileBaggageFromJourney(journey: App.JourneyV2): FlightTileBaggage {
  const fareFeatures = journey.fareFamily?.fareFeatures ?? []
  const carryOn = getIncludedCarryOn(fareFeatures)
  const checked = getIncludedCheckedBaggage(fareFeatures)

  return {
    carryOn: Boolean(carryOn),
    checked: Boolean(checked),
    carryOnLabel: carryOn?.description,
    checkedLabel: checked?.description,
  }
}
